import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import Dashboard from "./pages/Dashboard";
import LicensesPage from "./pages/LicensesPage";
import VerificationPage from "./pages/VerificationPage";
import MyInfoPage from "./pages/MyInfoPage";
import LeadsPage from "./pages/LeadsPage";
import LoginPage from "./pages/LoginPage";
import AppShell from "./components/layout/AppShell";
import { supabase } from "./supabaseClient";
import AdminDashboard from "./pages/AdminDashboard";
import PublicSearch from "./pages/PublicSearch";
import PublicFindPro from "./pages/PublicFindPro";
import PublicProfile from "./pages/PublicProfile";
import PrivacyPage from "./pages/PrivacyPage";
import TOSPage from "./pages/TOSPage";
import InvitePage from "./pages/InvitePage";
import ClaimPage from "./pages/ClaimPage";
import PricingPage from "./pages/PricingPage";
import PlansPage from "./pages/PlansPage";
import TrialInvitePage from "./pages/TrialInvitePage";
import { buildJsonLd, getSeoForPath } from "./utils/seo";
import AboutPage from "./pages/AboutPage";
import AffiliatePage from "./pages/AffiliatePage";
// import FloatingA11yPromo from "./components/FloatingA11yPromo";
import NotFoundPage from "./pages/NotFoundPage";
import useRouteFocus from "./hooks/useRouteFocus";
import TrustSessionPage from "./pages/TrustSessionPage";
import TrustSessionHostPage from "./pages/TrustSessionHostPage";
import VerbalConsentSmsPage from "./pages/VerbalConsentSmsPage";
import SmsTermsPrivacyPage from "./pages/SmsTermsPrivacyPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import AffiliateRedirectPage from "./pages/AffiliateRedirectPage";
import {
  attributeAffiliateSignup,
  captureAffiliateReferral,
  getAffiliateCodeFromSearch,
} from "./utils/affiliate";

function RootRoute({ session }) {
  const location = useLocation();
  const hasQuery = !!location.search && location.search !== "?";
  if (session && !hasQuery) {
    return <Navigate to="/dashboard" replace />;
  }
  return <PublicSearch />;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
}

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const base =
      (process.env.REACT_APP_PUBLIC_SITE_URL ||
        (typeof window !== "undefined" && window.location?.origin) ||
        "https://www.clearbadge.org")
        .replace(/\/$/, "");
    const pathname = location.pathname || "/";
    const meta = getSeoForPath(pathname);
    const canonicalHref = `${base}${pathname}`;

    const setMetaTag = ({ name, property, content }) => {
      const selector = name ? `meta[name='${name}']` : `meta[property='${property}']`;
      let tag = document.querySelector(selector);
      const hasContent = content !== undefined && content !== null && content !== "";
      if (!hasContent) {
        if (tag) tag.remove();
        return;
      }
      if (!tag) {
        tag = document.createElement("meta");
        if (name) tag.setAttribute("name", name);
        if (property) tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      if (tag.getAttribute("content") !== content) {
        tag.setAttribute("content", content);
      }
    };

    const setLink = ({ rel, href }) => {
      if (!href) return;
      let link = document.querySelector(`link[rel='${rel}']`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      if (link.getAttribute("href") !== href) {
        link.setAttribute("href", href);
      }
    };

    const setJsonLd = (id, data) => {
      if (!data) return;
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("id", id);
        document.head.appendChild(script);
      }
      const next = JSON.stringify(data);
      if (script.text !== next) {
        script.text = next;
      }
    };

    document.title = meta.title;
    setMetaTag({ name: "description", content: meta.description });
    setMetaTag({ name: "keywords", content: meta.keywords });
    const robotsContent = meta.indexable
      ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      : "noindex, nofollow, noarchive";
    setMetaTag({ name: "robots", content: robotsContent });
    setMetaTag({ name: "googlebot", content: robotsContent });

    setMetaTag({ property: "og:type", content: meta.ogType || "website" });
    setMetaTag({ property: "og:site_name", content: "ClearBadge" });
    setMetaTag({ property: "og:locale", content: "en_US" });
    setMetaTag({ property: "og:title", content: meta.title });
    setMetaTag({ property: "og:description", content: meta.description });
    setMetaTag({ property: "og:url", content: canonicalHref });
    setMetaTag({ property: "og:image", content: meta.image });
    setMetaTag({ property: "og:image:secure_url", content: meta.image });
    setMetaTag({ property: "og:image:type", content: meta.imageType });
    setMetaTag({ property: "og:image:width", content: meta.imageWidth ? String(meta.imageWidth) : "" });
    setMetaTag({ property: "og:image:height", content: meta.imageHeight ? String(meta.imageHeight) : "" });
    setMetaTag({ property: "og:image:alt", content: meta.imageAlt });

    setMetaTag({ name: "twitter:card", content: "summary_large_image" });
    setMetaTag({ name: "twitter:title", content: meta.title });
    setMetaTag({ name: "twitter:description", content: meta.description });
    setMetaTag({ name: "twitter:url", content: canonicalHref });
    setMetaTag({ name: "twitter:image", content: meta.image });
    setMetaTag({ name: "twitter:image:alt", content: meta.imageAlt });

    setLink({ rel: "canonical", href: canonicalHref });

    const jsonLd = buildJsonLd({ baseUrl: base, canonical: canonicalHref, meta, pathname });
    setJsonLd("seo-jsonld", jsonLd);
  }, [location.pathname]);

  return null;
}

function RouteFocusManager() {
  useRouteFocus();
  return null;
}

function AffiliateAttributionManager() {
  const location = useLocation();
  const capturedRef = useRef("");

  useEffect(() => {
    const code = getAffiliateCodeFromSearch(location.search);
    if (!code) return;
    const key = `${code}:${location.pathname}:${location.search}`;
    if (capturedRef.current === key) return;
    capturedRef.current = key;
    captureAffiliateReferral({
      code,
      landingPath: `${location.pathname}${location.search}`,
      referrer: document.referrer || "",
    });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const [session, setSession] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const ensuredProfileRef = useRef(null);

  useEffect(() => {
    const ensureProfileForSession = async (s) => {
      const userId = s?.user?.id;
      if (!userId) return;
      if (ensuredProfileRef.current === userId) return;

      const { data: profileRow, error: fetchError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (fetchError) {
        console.warn("Could not load profile for session:", fetchError.message);
        return;
      }

      if (profileRow?.id) {
        ensuredProfileRef.current = userId;
        try {
          await attributeAffiliateSignup(s);
        } catch (err) {
          console.warn("Affiliate attribution warning:", err.message);
        }
        return;
      }

      const { error: upsertError } = await supabase
        .from("profiles")
        .upsert([{ id: userId, email: s.user?.email || null, created_at: new Date().toISOString() }], { onConflict: "id" });

      if (upsertError) {
        console.warn("Profile bootstrap warning:", upsertError.message);
      } else {
        ensuredProfileRef.current = userId;
      }

      try {
        await attributeAffiliateSignup(s);
      } catch (err) {
        console.warn("Affiliate attribution warning:", err.message);
      }
    };

    const fetchAdminStatus = async (userId) => {
      if (!userId) {
        setIsAdmin(false);
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", userId)
        .maybeSingle();
      setIsAdmin(!!profile?.is_admin);
    };

    const initSession = async () => {
      const { data } = await supabase.auth.getSession();
      const currentSession = data.session;
      setSession(currentSession);
      if (currentSession?.user?.id) {
        await ensureProfileForSession(currentSession);
        await fetchAdminStatus(currentSession.user.id);
      } else {
        setIsAdmin(false);
      }
      setSessionChecked(true);
    };
    initSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user?.id) {
        ensureProfileForSession(s);
        fetchAdminStatus(s.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  if (!sessionChecked) {
    return null;
  }

  return (
    <Router>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <ScrollToTop />
      <SeoManager />
      <RouteFocusManager />
      <AffiliateAttributionManager />
      {/* Temporarily disabled platform-wide; keep component in codebase for later reuse. */}
      {/* <FloatingA11yPromo /> */}
      <Routes>
        <Route path="/p/:slug" element={<PublicProfile />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invite/:id" element={<InvitePage />} />
        <Route path="/claim/:id" element={<ClaimPage />} />
        <Route path="/" element={<RootRoute session={session} />} />
        <Route path="/find-a-pro" element={<PublicFindPro />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/trial-invite" element={<TrialInvitePage />} />
        <Route path="/elite-trial-invite" element={<TrialInvitePage tier="elite" />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/affiliate" element={<AffiliatePage />} />
        <Route path="/ref/:code" element={<AffiliateRedirectPage />} />
        <Route path="/verbal-consent-sms" element={<VerbalConsentSmsPage />} />
        <Route path="/smstos" element={<SmsTermsPrivacyPage />} />
        <Route path="/login" element={<LoginPage session={session} />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path="/terms-of-service" element={<TOSPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/s/:token" element={<TrustSessionPage />} />
        <Route
          element={session ? <AppShell /> : <Navigate to="/login" replace />}
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/licenses" element={<LicensesPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/verification" element={<VerificationPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/my-info" element={<MyInfoPage />} />
          <Route path="/trust-session/:id" element={<TrustSessionHostPage />} />
          <Route path="/lookup" element={<PublicSearch embedded />} />
          <Route path="/find-pro" element={<PublicFindPro embedded />} />
          {isAdmin && <Route path="/admin" element={<AdminDashboard />} />}
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;
